import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";
import { EmailDocMode, docFilename } from "@/components/common/EmailDocMode";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// Email Work Order modal. Renders a contact picker (radio list of
// site contacts with email) + manual-email input + an optional
// "Include Review & Sign link" toggle (hidden when emailing a
// signed PDF). Extracted from WorkOrderTab.jsx (Feb 2026, iter179)
// State stays with the parent; this component is purely
// presentational.
export function WOEmailDialog({
  open, onOpenChange,
  emailType,
  siteContacts,
  selectedContactEmail, setSelectedContactEmail,
  includeWOSignLink, setIncludeWOSignLink,
  editingWO,
  onSend, docMode, setDocMode, woNumber,
}) {
  const contactsWithEmail = siteContacts.filter(c => c.email);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {emailType === "signed" ? "Email Signed Work Order" : "Email Work Order"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <EmailDocMode mode={docMode} onChange={setDocMode} testId="wo-email-doc-mode"
            filename={docFilename(emailType === "signed" ? "Signed_Work_Order" : "Work_Order", woNumber)} />

          <div>
            <Label className="text-xs font-medium">Send To</Label>
            {contactsWithEmail.length > 0 ? (
              <div className="space-y-2 mt-2">
                {contactsWithEmail.map((contact) => (
                  <label
                    key={contact.id}
                    className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                      selectedContactEmail === contact.email
                        ? "border-blue-400 bg-blue-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="radio" name="contact-email" value={contact.email}
                      checked={selectedContactEmail === contact.email}
                      onChange={(e) => setSelectedContactEmail(e.target.value)}
                      className="accent-blue-600"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {contact.name}
                        {contact.is_primary && <span className="text-xs text-blue-600"> (Primary)</span>}
                      </div>
                      <div className="text-xs text-slate-500">{contact.email}</div>
                    </div>
                  </label>
                ))}
                <div className="pt-2">
                  <Label className="text-xs">Or Enter Email Manually</Label>
                  <Input
                    type="email"
                    value={contactsWithEmail.some(c => c.email === selectedContactEmail) ? "" : selectedContactEmail}
                    onChange={(e) => setSelectedContactEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="mt-1"
                    data-testid="input-manual-email"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <Input
                  type="email"
                  value={selectedContactEmail}
                  onChange={(e) => setSelectedContactEmail(e.target.value)}
                  placeholder="email@example.com"
                  data-testid="input-manual-email"
                />
              </div>
            )}
          </div>

          {emailType !== "signed" && editingWO && (
            <label className="flex items-center gap-2 cursor-pointer" data-testid="include-wo-sign-link-toggle">
              <input
                type="checkbox"
                checked={includeWOSignLink}
                onChange={(e) => setIncludeWOSignLink(e.target.checked)}
                className="h-4 w-4 accent-blue-600 rounded"
              />
              <span className="text-sm text-slate-700">Include Review & Sign link in email</span>
            </label>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={onSend} data-testid="btn-send-email">
              <Mail className="w-4 h-4 mr-1" /> Download PDF & Open Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
